import {
  VALOR_VACIO_DE_COLUMNA,
  escribirFiltrosPorColumna,
  leerFiltrosPorColumna,
  unirValores,
  valoresDeFiltro,
  type FiltroDeColumna,
} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {tr, trx} from '../i18n/idioma';
import type {ColumnaConFiltro, UseOpcionesDeColumna} from '../utilidades/columnas-con-filtro';
import {mes} from '../utilidades/formato';
import {SelectorMultiple} from './selector-multiple';

// Filtros por columna (Daniel, 7/10/2026): debajo de los fijos, uno por cada
// columna agregada a la tabla. Texto y mes: lista de valores; numero y fecha: rango.
export function FiltrosPorColumna({
  columnas,
  valor,
  onCambiar,
  useOpciones,
  ayuda,
  abiertos: abiertosAlInicio = false,
}: {
  columnas: ColumnaConFiltro[];
  valor: string | undefined;
  onCambiar: (valor: string | undefined) => void;
  useOpciones: UseOpcionesDeColumna;
  // De donde salen las columnas, si no es la tabla de la misma pantalla.
  ayuda?: string;
  // Abiertos desde el comienzo (cuando son los unicos filtros de la tabla).
  abiertos?: boolean;
}) {
  const filtros = leerFiltrosPorColumna(valor);
  const puestos = Object.keys(filtros).length;
  // Plegados para no llenar la pantalla; abiertos si alguno esta puesto.
  const [abiertos, setAbiertos] = useState(abiertosAlInicio || puestos > 0);
  if (!columnas.length) return null;
  const cambiar = (id: string, filtro: FiltroDeColumna | undefined) => {
    const nuevos = {...filtros};
    if (filtro) nuevos[id] = filtro;
    else delete nuevos[id];
    onCambiar(escribirFiltrosPorColumna(nuevos));
  };
  return (
    <div className="mt-2 border-t border-linea/70 pt-2" data-prueba="filtros-por-columna">
      <div className="flex flex-wrap items-baseline gap-2 text-xs text-apagado">
        <button
          type="button"
          aria-expanded={abiertos}
          onClick={() => setAbiertos(v => !v)}
          className="rounded-md border border-linea bg-white px-2 py-1 text-xs text-tinta hover:bg-papel"
        >
          {abiertos ? '▴ ' : '▾ '}
          {tr('Filtros de las columnas de la tabla', 'Table column filters')} ({columnas.length})
          {puestos > 0 && <span className="ml-1 font-semibold text-verde-oscuro">· {puestos}</span>}
        </button>
        {puestos > 0 && (
          <button type="button" onClick={() => onCambiar(undefined)} className="text-verde hover:underline">
            {tr('Quitar los filtros de columnas', 'Clear column filters')}
          </button>
        )}
        {abiertos && (
          <span>
            {ayuda ??
              tr(
                'Uno por cada columna de la tabla; agrega o quita columnas en "Columnas".',
                'One per table column; add or remove columns in "Columns".',
              )}
          </span>
        )}
      </div>
      {abiertos && (
        <div className="mt-2 grid items-end gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
          {columnas.map(c =>
            c.tipo === 'texto' || c.tipo === 'mes' ? (
              <FiltroDeValores
                key={c.id}
                columna={c}
                filtro={filtros[c.id]}
                onCambiar={f => cambiar(c.id, f)}
                useOpciones={useOpciones}
              />
            ) : (
              <FiltroDeRango key={c.id} columna={c} filtro={filtros[c.id]} onCambiar={f => cambiar(c.id, f)} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function FiltroDeValores({
  columna,
  filtro,
  onCambiar,
  useOpciones,
}: {
  columna: ColumnaConFiltro;
  filtro: FiltroDeColumna | undefined;
  onCambiar: (filtro: FiltroDeColumna | undefined) => void;
  useOpciones: UseOpcionesDeColumna;
}) {
  const [abierta, setAbierta] = useState(false);
  const [escrito, setEscrito] = useState('');
  const [contiene, setContiene] = useState('');
  // La busqueda en el servidor espera a que se deje de escribir.
  useEffect(() => {
    const temporizador = setTimeout(() => setContiene(escrito.trim()), 300);
    return () => clearTimeout(temporizador);
  }, [escrito]);
  const opciones = useOpciones(columna.id, contiene, abierta);
  const etiqueta = (v: string) =>
    v === VALOR_VACIO_DE_COLUMNA ? tr('(vacío)', '(empty)') : columna.tipo === 'mes' ? mes(v) : v;
  return (
    <SelectorMultiple
      etiqueta={trx(columna.titulo)}
      valor={unirValores(filtro?.valores ?? [])}
      opciones={(opciones.data?.opciones ?? []).map(o => ({
        valor: o.valor,
        etiqueta: etiqueta(o.valor),
        cantidad: o.cantidad,
      }))}
      onCambiar={v => {
        const valores = valoresDeFiltro(v);
        onCambiar(valores.length ? {valores} : undefined);
      }}
      onAbrir={() => setAbierta(true)}
      onBuscar={setEscrito}
      nota={
        opciones.isFetching && !opciones.data
          ? tr('Cargando…', 'Loading…')
          : opciones.data?.truncado
            ? tr('Hay más valores: escribe para buscar.', 'There are more values: type to search.')
            : undefined
      }
    />
  );
}

function FiltroDeRango({
  columna,
  filtro,
  onCambiar,
}: {
  columna: ColumnaConFiltro;
  filtro: FiltroDeColumna | undefined;
  onCambiar: (filtro: FiltroDeColumna | undefined) => void;
}) {
  const [desde, setDesde] = useState(filtro?.desde ?? '');
  const [hasta, setHasta] = useState(filtro?.hasta ?? '');
  useEffect(() => {
    setDesde(filtro?.desde ?? '');
    setHasta(filtro?.hasta ?? '');
  }, [filtro?.desde, filtro?.hasta]);
  const aplicar = (d: string, h: string) => {
    if (d === (filtro?.desde ?? '') && h === (filtro?.hasta ?? '')) return;
    onCambiar(d || h ? {...(d ? {desde: d} : {}), ...(h ? {hasta: h} : {})} : undefined);
  };
  const esFecha = columna.tipo === 'fecha';
  const campo = `w-full min-w-0 rounded-md border bg-white px-1.5 py-1 text-sm text-tinta ${
    filtro ? 'border-verde' : 'border-linea'
  }`;
  const titulo = trx(columna.titulo);
  return (
    <fieldset className="block min-w-0 text-xs text-apagado">
      <legend>{titulo}</legend>
      <div className="mt-1 flex gap-1">
        <input
          type={esFecha ? 'date' : 'number'}
          step="any"
          value={desde}
          aria-label={tr(`${titulo} desde`, `${titulo} from`)}
          placeholder={tr('desde', 'from')}
          onChange={e => {
            setDesde(e.target.value);
            if (esFecha) aplicar(e.target.value, hasta);
          }}
          onBlur={() => aplicar(desde, hasta)}
          onKeyDown={e => e.key === 'Enter' && aplicar(desde, hasta)}
          className={campo}
        />
        <input
          type={esFecha ? 'date' : 'number'}
          step="any"
          value={hasta}
          aria-label={tr(`${titulo} hasta`, `${titulo} to`)}
          placeholder={tr('hasta', 'to')}
          onChange={e => {
            setHasta(e.target.value);
            if (esFecha) aplicar(desde, e.target.value);
          }}
          onBlur={() => aplicar(desde, hasta)}
          onKeyDown={e => e.key === 'Enter' && aplicar(desde, hasta)}
          className={campo}
        />
      </div>
    </fieldset>
  );
}
